import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import ColumnFilterMenu from "@/components/ColumnFilterMenu";
import { euro } from "@/lib/api";
import { hierTotal } from "@/lib/hierTotal";
import { ArrowUp, ArrowDown } from "lucide-react";

const stato = (cont, ric) => {
  const base = cont || ric || 1;
  const scost = (ric - cont) / base;
  if (scost < -0.1) return { label: "Sotto-fatturato", cls: "bg-rose-50 text-rose-700 border-rose-200" };
  if (scost > 0.1) return { label: "Sovra-fatturato", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Equilibrato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
};

const deltaCls = (d) => (d < 0 ? "text-rose-600" : "text-emerald-600");

const COLS = [
  { key: "nome", label: "Commessa", type: "text", align: "left" },
  { key: "contabilizzato", label: "Contabilità", type: "num", align: "right" },
  { key: "riconosciuto", label: "Riconosciuto", type: "num", align: "right" },
  { key: "delta", label: "Delta", type: "num", align: "right" },
  { key: "ritenuta", label: "Ritenute a garanzia", type: "num", align: "right" },
  { key: "recuperato", label: "Ritenute recuperate", type: "num", align: "right" },
  { key: "stato", label: "Stato", type: "text", align: "center" },
];

const raw = (row, c) => {
  if (c.key === "delta") return String(Math.round((row.riconosciuto - row.contabilizzato) * 100) / 100);
  if (c.key === "stato") return stato(row.contabilizzato, row.riconosciuto).label;
  if (c.type === "num") return String(Math.round((row[c.key] || 0) * 100) / 100);
  return (row[c.key] || "").toString();
};
const disp = (r, c) => c.type === "num" ? euro(parseFloat(r)) : (r || "—");

export default function BefTable({ benestare }) {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [filters, setFilters] = useState({});

  const enriched = useMemo(() => benestare.map((c) => ({ ...c, delta: c.riconosciuto - c.contabilizzato, stato: stato(c.contabilizzato, c.riconosciuto).label })), [benestare]);

  const distinct = useMemo(() => {
    const d = {};
    COLS.forEach((c) => { d[c.key] = Array.from(new Set(enriched.map((r) => raw(r, c)))).sort((a, b) => c.type === "num" ? parseFloat(a) - parseFloat(b) : String(a).localeCompare(String(b), "it", { numeric: true })); });
    return d;
  }, [enriched]);

  const rows = useMemo(() => {
    let r = enriched.filter((row) => COLS.every((c) => { const s = filters[c.key]; return !s || s.has(raw(row, c)); }));
    // Raggruppa le sottocommesse sotto la madre, poi ordina gerarchicamente (madri tra loro, figli dentro la madre)
    const groups = [];
    let cur = null;
    r.forEach((row) => {
      if (!row.is_sub) { cur = { parent: row, children: [] }; groups.push(cur); }
      else if (cur) cur.children.push(row);
      else groups.push({ parent: null, children: [row] });
    });
    if (sort.key) {
      const col = COLS.find((c) => c.key === sort.key);
      const mul = sort.dir === "asc" ? 1 : -1;
      const cmp = (a, b) => col.type === "num"
        ? ((a[sort.key] || 0) - (b[sort.key] || 0)) * mul
        : String(a[sort.key] || "").localeCompare(String(b[sort.key] || ""), "it", { numeric: true }) * mul;
      groups.forEach((g) => g.children.sort(cmp));
      groups.sort((ga, gb) => cmp(ga.parent || ga.children[0] || {}, gb.parent || gb.children[0] || {}));
    }
    const out = [];
    groups.forEach((g) => { if (g.parent) out.push(g.parent); g.children.forEach((c) => out.push(c)); });
    return out;
  }, [enriched, filters, sort]);

  const toggle = (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
  const ht = (g) => hierTotal(enriched, rows, g);
  const tot = { c: ht((x) => x.contabilizzato), r: ht((x) => x.riconosciuto), rit: ht((x) => x.ritenuta), rec: ht((x) => x.recuperato) };
  tot.d = tot.r - tot.c;

  if (benestare.length === 0)
    return <p className="text-sm text-slate-400 py-8 text-center">Nessun dato di benestare/contabilità disponibile.</p>;

  return (
    <div className="overflow-auto flex-1 min-h-0">
      <table className="w-full border-collapse text-sm" data-testid="bef-table">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-100">
            {COLS.map((c) => (
              <th key={c.key} data-testid={`bef-th-${c.key}`}
                className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 whitespace-nowrap border-b border-slate-200 ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"}`}>
                <span className={`inline-flex items-center gap-0.5 ${c.align === "right" ? "flex-row-reverse" : c.align === "center" ? "justify-center" : ""}`}>
                  <button onClick={() => toggle(c.key)} className="inline-flex items-center gap-1 hover:text-slate-800">
                    {c.label}
                    {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                  <ColumnFilterMenu testid={`bef-${c.key}`} values={distinct[c.key]} selected={filters[c.key]}
                    format={(v) => disp(v, c)} onChange={(s) => setFilters((f) => ({ ...f, [c.key]: s }))} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => {
            const st = stato(c.contabilizzato, c.riconosciuto);
            return (
              <tr key={c.nome} data-testid={`bef-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className={`px-3 py-2 ${c.is_sub ? "text-slate-500 pl-6" : "text-slate-700 font-medium"}`}>{c.nome}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-800">{euro(c.contabilizzato)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-800">{euro(c.riconosciuto)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold ${deltaCls(c.delta)}`}>{euro(c.delta)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-700" data-testid={`bef-ritenuta-${i}`}>{c.ritenuta ? euro(c.ritenuta) : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-600" data-testid={`bef-recuperato-${i}`}>{c.recuperato ? euro(c.recuperato) : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-center"><Badge variant="outline" className={`font-normal ${st.cls}`} data-testid={`bef-stato-${i}`}>{st.label}</Badge></td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="sticky bottom-0">
          <tr className="bg-slate-100 font-semibold border-t border-slate-200">
            <td className="px-3 py-2 text-slate-700 uppercase text-[10px] tracking-widest">Totale</td>
            <td className="px-3 py-2 text-right tabular-nums text-blue-800">{euro(tot.c)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-blue-800">{euro(tot.r)}</td>
            <td className={`px-3 py-2 text-right tabular-nums font-semibold ${deltaCls(tot.d)}`}>{euro(tot.d)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-amber-700">{euro(tot.rit)}</td>
            <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{euro(tot.rec)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
